import { useEffect, useState, useCallback } from "react";
import { Users, ShieldAlert, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/** "Who is this app for?" — the audience toggles at the top of an app's
 *  settings, and the only place an app is opened up.
 *
 *  Super User is always on and can't be switched off: an app with nothing else
 *  ticked is a Super User app, blocked in code for everyone else (Iter 264).
 *  Unlocking it means ticking Staff or a partner audience right here. */
export function AppAudiencePicker({ value = [], onChange, appId }) {
  const [audiences, setAudiences] = useState([]);
  const [reach, setReach] = useState(null);

  useEffect(() => {
    api.get("/app-audiences")
      .then((r) => setAudiences((r.data.audiences || []).filter((a) => a.active !== false)))
      .catch(() => setAudiences([]));
  }, []);

  const picked = Array.isArray(value) ? value : [];
  // Super User carries no reach of its own — it's the floor, not an audience
  // you can widen to.
  const opened = picked.filter((id) => id && id !== "super");
  const key = opened.join(",");
  const unlocked = opened.length > 0;

  // Keep the app's own list honest: every app is a Super User app at minimum.
  useEffect(() => {
    if (audiences.length && !picked.includes("super")) onChange([...picked, "super"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiences.length, key]);

  useEffect(() => {
    api.get(`/app-audiences/reach?ids=${encodeURIComponent(key)}`)
      .then((r) => setReach(r.data)).catch(() => setReach(null));
  }, [key]);

  // Iter 249 — an org-level deny is checked BEFORE the permission check, so a
  // leftover deny silently beats a widened audience (Sunco lost KB2 that way).
  // Surface it here, where the widening happens, with a one-click clear.
  const [stale, setStale] = useState([]);
  const [clearing, setClearing] = useState(false);
  const loadStale = useCallback(() => {
    if (!appId) { setStale([]); return; }
    api.get(`/apps/${appId}/stale-denies?audiences=${encodeURIComponent(key)}`)
      .then((r) => setStale(r.data.orgs || [])).catch(() => setStale([]));
  }, [appId, key]);
  useEffect(() => { loadStale(); }, [loadStale]);

  const clearStale = async () => {
    setClearing(true);
    try {
      const { data } = await api.post(`/apps/${appId}/stale-denies/clear?audiences=${encodeURIComponent(key)}`);
      toast.success(`Cleared the deny for ${data.cleared.length} organization${data.cleared.length === 1 ? "" : "s"}`);
      loadStale();
    } catch {
      toast.error("Could not clear those denies");
    } finally { setClearing(false); }
  };

  const toggle = (id) => {
    if (id === "super") return;                 // the floor, not a choice
    onChange(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/50"
      data-testid="app-audience-picker">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-slate-400" />
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Who is this app for?</div>
        {!unlocked && (
          <span data-testid="app-audience-super-only"
            className="flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white dark:bg-slate-200 dark:text-slate-900">
            <Lock className="h-2.5 w-2.5" /> Super user app
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {audiences.map((a) => {
          const isSuper = a.audience_id === "super";
          const on = isSuper || picked.includes(a.audience_id);
          return (
            <button key={a.audience_id} type="button" disabled={isSuper}
              title={isSuper ? "Super admins always have access — this can't be switched off" : a.description}
              data-testid={`app-audience-toggle-${a.audience_id}`}
              onClick={() => toggle(a.audience_id)}
              className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${on
                ? "border-primary bg-primary/10 font-semibold text-primary"
                : "border-slate-200 bg-white text-slate-500 hover:border-primary/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"} ${isSuper ? "cursor-default" : ""}`}>
              {isSuper && <Lock className="h-3 w-3" />}
              {a.label}
            </button>
          );
        })}
      </div>
      {stale.length > 0 && (
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 dark:border-amber-500/40 dark:bg-amber-500/10"
             data-testid="app-audience-stale-denies">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1 text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">
              <span className="font-semibold">
                {stale.length} organization{stale.length === 1 ? " is" : "s are"} individually denied this app and will still not see it
              </span>
              {" — "}
              {stale.map((o) => `${o.name} (${o.blocked_users} ${o.blocked_users === 1 ? "person" : "people"})`).join(", ")}.
              {" Denies are set per organization and override the audiences above."}
            </div>
            <button type="button" onClick={clearStale} disabled={clearing}
                    data-testid="app-audience-clear-denies"
                    className="shrink-0 rounded border border-amber-400 bg-white px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60 dark:bg-transparent dark:text-amber-200">
              {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Clear the denies"}
            </button>
          </div>
        </div>
      )}
      <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
        <span data-testid="app-audience-reach">
          {!unlocked
            ? "Locked to super users — tick Staff or a partner audience above to open it up. "
            : reach ? (
              <>
                {audiences.filter((a) => opened.includes(a.audience_id))
                  .map((a) => `${reach.counts?.[a.audience_id] ?? 0} ${a.label.toLowerCase()}`)
                  .join(", ")}
                {" — "}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {reach.total} {reach.total === 1 ? "person" : "people"}
                </span>{" "}
                can see it.{" "}
              </>
            ) : null}
        </span>
        Super admins always have access. Audiences are managed in{" "}
        <span className="font-medium text-primary">Settings → App Audiences</span>; their default permissions in{" "}
        <span className="font-medium text-primary">Settings → Permissions → Audiences</span>.
      </p>
    </div>
  );
}
